import Vue from 'vue';
import {
  findIndex, differenceBy, sortBy,
} from 'lodash';
import api from 'src/api';
import { l, inputAttachments } from 'src/utils';
import { isExternalOperator } from 'src/store/modules/user';

// в константы выносим только названия мутаций/геттеров/экшенов,
// которые переиспользуем/планируем переиспользовать
export const fetchActivity = 'fetchActivity';
export const updateAppeal = 'updateAppeal';

export const ACTIVITY_MESSAGE_ADD = 'ACTIVITY_MESSAGE_ADD';
export const ACTIVITY_MESSAGE_UPDATE = 'ACTIVITY_MESSAGE_UPDATE';
export const CREATE_BLANK_ACTIVITY = 'CREATE_BLANK_ACTIVITY';
export const UPDATE_ACTIVITY = 'UPDATE_ACTIVITY';
export const SET_STOP_ACTIVITY_FETCHING = 'SET_STOP_ACTIVITY_FETCHING';
export const SET_ACTIVITY_FETCHING = 'SET_ACTIVITY_FETCHING';
export const SET_ACTIVITY_LOADED = 'SET_ACTIVITY_LOADED';
export const MARKERS_UPDATE = 'MARKERS_UPDATE';
export const UPDATE_ACTIVITIES_BY_CUSTOMER = 'UPDATE_ACTIVITIES_BY_CUSTOMER';
export const REMOVE_ACTIVITY = 'REMOVE_ACTIVITY';
export const ADD_MESSAGES = 'ADD_MESSAGES';
export const APPEAL_STATUS_REMOVE = 'APPEAL_STATUS_REMOVE';
export const ADD_INPUT_ATTACHMENTS = 'ADD_INPUT_ATTACHMENTS';
export const UPDATE_INPUT_ATTACHMENTS = 'UPDATE_INPUT_ATTACHMENTS';
export const REMOVE_INPUT_ATTACHMENTS = 'REMOVE_INPUT_ATTACHMENTS';
export const CLEAR_INPUT_ATTACHMENTS = 'CLEAR_INPUT_ATTACHMENTS';
export const CLEAR_INPUT_ATTACHMENTS_ALL = 'CLEAR_INPUT_ATTACHMENTS_ALL';

export const getActivityById = 'getActivityById';
export const getAttachmentById = 'getAttachmentById';

const data = {
  activities: [],
  appealIsClosed: [],
  inputAttachments: {},
};

function createBlank() {
  return {
    state: undefined,
    channel: undefined,
    channelType: undefined,
    channelName: undefined,
    requestType: undefined,
    requestTypeId: undefined,
    avatarUrl: null,
    case: {
      id: undefined,
      name: undefined,
      description: undefined,
      insertedAt: new Date(0),
      updatedAt: new Date(0),
      comment: undefined,
    },
    customer: {
      id: undefined,
      firstName: undefined,
      lastName: undefined,
      middleName: undefined,
      identified: false,
    },
    operator: {
      id: undefined,
      name: false,
      firstName: undefined,
      lastName: undefined,
      middleName: undefined,
      avatarUrl: null,
    },
    messages: [],
    markers: [],
    customerOnline: undefined,
    insertedAt: new Date(0),
    fetchAt: new Date(0),
    fetching: false,
    syncAt: new Date(0),
    sync: false,
    isAuthorized: false,
    noAuthorized: false,
    appealId: undefined,
    redirectAppealReceived: false,
  };
}

function selectActivity(commit, getters, id) {
  if (typeof getters.getActivityById(id) === 'undefined') {
    commit(CREATE_BLANK_ACTIVITY, { id });
  }
  return getters.getActivityById(id);
}

const actions = {
  fetchActivity({ commit, getters, rootGetters }, { id, force = false, blank = false }) {
    let activity = selectActivity(commit, getters, id);
    const now = new Date();
    if ((activity && (now - activity.syncAt) > 5000) || force) {
      if (activity) commit(SET_ACTIVITY_FETCHING, activity);
      if (blank) {
        commit(REMOVE_ACTIVITY, { id });
        commit(CREATE_BLANK_ACTIVITY, { id });
        activity = getters.getActivityById(id);
      }

      const isExternalOperatorVal = rootGetters[`user/${isExternalOperator}`];
      return api.getActivity(id, isExternalOperatorVal).then((response) => {
        commit(SET_ACTIVITY_LOADED, {
          activity,
          response,
        });
      });
    }
    return activity;
  },
  takeActivityInWork({ commit, getters, rootGetters }, { id, state }) {
    const isExternalOperatorVal = rootGetters[`user/${isExternalOperator}`];
    return api.takeActivityInWork(id, state, isExternalOperatorVal).then((response) => {
      const activity = selectActivity(commit, getters, response.id);
      commit(SET_ACTIVITY_FETCHING, activity);
      commit(SET_ACTIVITY_LOADED, {
        activity,
        response,
      });
    });
  },
  createOutboundActivityByOperator(_, activity) {
    return api.createOutboundActivity(activity.previousActivityId, activity.subject, activity.requestTypeId);
  },
  transferActivity({ commit, getters }, {
    id, requestTypeId, comment, operatorId,
  }) {
    const activity = selectActivity(commit, getters, id);
    commit(SET_ACTIVITY_FETCHING, activity);
    return api.transferActivity(id, requestTypeId, comment, operatorId).then((response) => {
      commit(SET_ACTIVITY_LOADED, {
        activity,
        response,
      });
    });
  },
  closeActivityByOperator({ commit, getters }, id) {
    const activity = selectActivity(commit, getters, id);
    commit(SET_ACTIVITY_FETCHING, activity);
    return api.closeActivityByOperator(id).then(() => {
      commit(REMOVE_ACTIVITY, {
        id,
      });
    });
  },
  updateActivityCase({ commit, getters }, { id, caseId }) {
    const activity = selectActivity(commit, getters, id);
    commit(SET_ACTIVITY_FETCHING, activity);
    return api.updateActivityCase(id, caseId).then((response) => {
      commit(SET_ACTIVITY_LOADED, {
        activity,
        response,
      });
    });
  },
  createCaseFromActivity({ commit, getters }, { activityId, requestTypeId, case: caseInput }) {
    const activity = selectActivity(commit, getters, activityId);
    commit(SET_ACTIVITY_FETCHING, activity);
    return api.createCaseFromActivity(activityId, requestTypeId, caseInput).then((response) => {
      commit(SET_ACTIVITY_LOADED, {
        activity,
        response,
      });
    });
  },
  updateAppeal({ commit, getters }, { activityId, appealId }) {
    const activity = selectActivity(commit, getters, activityId);
    commit(SET_ACTIVITY_FETCHING, activity);
    return api.updateAppealId(activityId, appealId).then((response) => {
      commit(SET_ACTIVITY_LOADED, {
        activity,
        response,
      });
    });
  },
  onRedirectAppealReceived({ commit, getters }, activityId) {
    const activity = selectActivity(commit, getters, activityId);
    commit('SET_REDIRECT_APPEAL_TRUE', activity);
  },
  changeAppealStatus({ commit }, { activityId }) {
    commit('APPEAL_STATUS_UPDATE', { activityId });
  },
};

const mutations = {
  CREATE_BLANK_ACTIVITY(state, rest) {
    const activity = createBlank();
    mutations.UPDATE_ACTIVITY(state, { activity, response: rest });
    state.activities.push(activity);
  },
  REMOVE_ACTIVITY(state, { id }) {
    const { activities } = state;
    Vue.delete(state.activities, findIndex(activities, { id }));
  },
  SET_ACTIVITY_FETCHING(state, activity) {
    activity.fetching = true;
    activity.fetchAt = new Date();
  },
  SET_STOP_ACTIVITY_FETCHING(state, activity) {
    activity.fetching = false;
    activity.fetchAt = new Date(0);
  },
  SYNC(state, activity) {
    activity.sync = true;
    activity.syncAt = new Date();
  },
  UPDATE_ACTIVITY(state, { activity, response }) {
    const {
      id,
      duration,
      state: activityState,
      insertedAt,
      customerOnline,
      channel,
      channelType,
      channelName,
      requestType,
      case: activityCase,
      customer,
      operator,
      messages,
      markers,
      appealId,
      isAuthorized,
      noAuthorized,
      avatarUrl,
    } = response;

    if (id) activity.id = id;
    if (typeof customerOnline !== 'undefined') activity.customerOnline = customerOnline;
    if (activityState) activity.state = activityState;
    if (insertedAt) activity.insertedAt = new Date(insertedAt);
    if (avatarUrl) activity.avatarUrl = avatarUrl;
    if (channel) activity.channel = channel;
    if (channelType) activity.channelType = channelType;
    if (channelName) activity.channelName = channelName;
    if (requestType) {
      activity.requestType = requestType.name;
      activity.requestTypeId = requestType.id;
    }
    if (duration) activity.duration = duration;

    if (activityCase) {
      if (activityCase.id) activity.case.id = activityCase.id;
      if (activityCase.name) activity.case.name = activityCase.name;
      if (activityCase.description) activity.case.description = activityCase.description;
      if (activityCase.insertedAt) activity.case.insertedAt = activityCase.insertedAt;
      if (activityCase.updatedAt) activity.case.updatedAt = activityCase.updatedAt;
      if (activityCase.comment !== undefined) activity.case.comment = activityCase.comment;
    }
    if (customer) {
      if (customer.id) activity.customer.id = customer.id;
      if (customer.firstName) activity.customer.firstName = customer.firstName;
      if (customer.lastName) activity.customer.lastName = customer.lastName;
      if (customer.middleName) activity.customer.middleName = customer.middleName;
      if (customer.identified) activity.customer.identified = customer.identified;
      if (customer.accounts) activity.customer.accounts = customer.accounts;
      if (customer.customInfo) activity.customer.customInfo = customer.customInfo;
      if (customer.gender) activity.customer.gender = customer.gender;
      if (customer.birthdate) activity.customer.birthdate = customer.birthdate;
      if (customer.vip) activity.customer.vip = customer.vip;
    }
    if (operator) {
      if (operator.id) activity.operator.id = operator.id;
      if (operator.name) activity.operator.name = operator.name;
      if (operator.firstName) activity.operator.firstName = operator.firstName;
      if (operator.lastName) activity.operator.lastName = operator.lastName;
      if (operator.middleName) activity.operator.middleName = operator.middleName;
    }
    if (messages) {
      const historyMessages = differenceBy(activity.messages, messages, 'id').filter((msg) => !msg.temporaryId);
      const nonSendMessages = differenceBy(activity.messages, messages, 'id').filter((msg) => msg.temporaryId);
      activity.messages = [...historyMessages, ...messages, ...nonSendMessages];
    }
    if (markers) activity.markers = markers;
    if (appealId) activity.appealId = appealId;
    if (isAuthorized) activity.isAuthorized = isAuthorized;
    if (noAuthorized) activity.noAuthorized = noAuthorized;
  },
  UPDATE_ACTIVITIES_BY_CUSTOMER(state, customer) {
    const { id, firstName } = customer;
    state.activities.forEach((activity) => {
      if (parseInt(activity.customer.id, 10) === parseInt(id, 10)) {
        activity.customer.firstName = firstName;
      }
    });
  },
  SET_ACTIVITY_LOADED(state, { activity, response }) {
    mutations.UPDATE_ACTIVITY(state, { activity, response });
    mutations.SET_STOP_ACTIVITY_FETCHING(state, activity);
    mutations.SYNC(state, activity);
  },
  ACTIVITY_MESSAGE_ADD(state, { activity, message }) {
    const messageWithCaseId = {
      ...message,
      caseId: activity.case.id,
    };
    activity.messages.push(messageWithCaseId);
  },
  ADD_MESSAGES(state, { activity, messages }) {
    let activityMessages = activity.messages;
    activityMessages = messages.concat(activityMessages);
    activityMessages = activityMessages.filter((elem, index, self) => index === findIndex(
      self, (item) => item.id === elem.id,
    ));
    activity.messages = sortBy(activityMessages, (msg) => new Date(msg.date));
  },
  MARKERS_UPDATE(state, { activity, response }) {
    activity.markers = [...response];
  },
  ACTIVITY_MESSAGE_UPDATE(state, { message, response }) {
    const props = Object.keys(response);
    props.forEach((key) => Vue.set(message, key, response[key]));
  },
  SET_REDIRECT_APPEAL_TRUE(state, activity) {
    activity.redirectAppealReceived = true;
  },
  APPEAL_STATUS_UPDATE(state, { activityId }) {
    state.appealIsClosed.push(activityId);
  },
  APPEAL_STATUS_REMOVE(state, { activityId }) {
    const { appealIsClosed } = state;
    Vue.delete(state.appealIsClosed, appealIsClosed.indexOf(activityId));
  },
  SET_INPUT_ATTACHMENTS(state, { activityId, attachments }) {
    if (!attachments) {
      l.error('Attachments setting error.');
      return;
    }
    Vue.set(state.inputAttachments, activityId, attachments);
  },
  ADD_INPUT_ATTACHMENTS(state, { activityId, temporaryId, attachment }) {
    if (!attachment || !activityId || !temporaryId) {
      l.error(`Attachment adding error. Activity Id is ${activityId}`);
      return;
    }
    inputAttachments.add(activityId, temporaryId, attachment);
    Vue.set(state.inputAttachments, activityId, {
      ...state.inputAttachments[activityId],
      [temporaryId]: attachment,
    });
  },
  UPDATE_INPUT_ATTACHMENTS(state, { activityId, temporaryId, attachment }) {
    if (!attachment || !activityId || !temporaryId) {
      l.error(`Attachment updating error. Activity Id is ${activityId}`);
      return;
    }
    inputAttachments.update(activityId, temporaryId, attachment);
    Vue.set(state.inputAttachments[activityId], temporaryId, {
      ...state.inputAttachments[activityId][temporaryId],
      ...attachment,
    });
  },
  REMOVE_INPUT_ATTACHMENTS(state, { activityId, temporaryId }) {
    if (!activityId || !temporaryId) {
      l.error(`Attachment removing error. Activity Id is ${activityId}`);
      return;
    }
    if (state.inputAttachments[activityId] && state.inputAttachments[activityId][temporaryId]) {
      Vue.delete(state.inputAttachments[activityId], temporaryId);
      inputAttachments.remove(activityId, temporaryId);
    }
  },
  CLEAR_INPUT_ATTACHMENTS(state, { activityId }) {
    if (!activityId) {
      l.error(`Attachment clearning error. Activity Id is ${activityId}`);
      return;
    }
    if (state.inputAttachments[activityId]) {
      Vue.delete(state.inputAttachments, activityId);
      inputAttachments.clear(activityId);
    }
  },
  CLEAR_INPUT_ATTACHMENTS_ALL(state) {
    if (state.inputAttachments) {
      state.inputAttachments = {};
      inputAttachments.clearAll();
    }
  },
};

const getters = {
  getActivityById(state) {
    const { activities } = state;
    return (id) => (activities[findIndex(activities, { id })]);
  },
  getAttachmentById(state) {
    const { inputAttachments: attachments } = state;
    return ({ activityId, temporaryId }) => attachments[activityId] && attachments[activityId][temporaryId];
  },
};

const store = {
  namespaced: true,
  state: data,
  actions,
  mutations,
  getters,
};

export default store;
